import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { Grid, Typography } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { apiEndpoints } from "generated/apiEndpoints"
import React, { useEffect } from "react"
import { StyledFormLabel } from "shared/styled/Global"
import { UploadData } from "helpers/CustomField/file.interfaces"
import { IOptionProps } from "../FormOption/formoption.interface"
import FormBigRadioButtons from "./FormBigRadioButtons/FormBigRadioButtons"
import { IRadioCardGroupProps } from "./FormBigRadioButtons/FormBigRadioButtons.interfaces"
import FormCardButton from "./FormCardButton/FormCardButton"
import { IFormCardButtonProps } from "./FormCardButton/FormCardButton.interfaces"
import FormCheckbox from "./FormCheckbox/FormCheckbox"
import { IFormCheckboxProps } from "./FormCheckbox/formchecklist.interfaces"
import FormDateTimePicker from "./FormDateTimePicker/FormDateTimePicker"
import { IFormDateFieldProps } from "./FormDateTimePicker/formdatetimepicker.interface"
import FormDepartmentPicker from "./FormDepartmentPicker/FormDepartmentPicker"
import FormLocationPicker from "./FormLocationPicker/FormLocationPicker"
import { IFormLocationPickerProps } from "./FormLocationPicker/formlocationpicker.interface"
import FormSelect from "./FormSelect/FormSelect"
import { IFormSelectProps } from "./FormSelect/formselect.interface"
import { FormSwitch } from "./FormSwitch/FormSwitch"
import { IFormSwitchProps } from "./FormSwitch/FormSwitch.interfaces"
import FormTextField from "./FormTextField/FormTextField"
import { IFormTextFieldProps } from "./FormTextField/formtextfield.interface"
import FormUploadFile from "./FormUploadFile/FormUploadFile"
import FormUploadPreviewList from "./FormUploadFile/FormUploadPreviewList"
import { IFormUploadAttachmentProps, IFormUploadFileProps } from "./FormUploadFile/formuploadfile.interface"
import FormVehiclePicker from "./FormVehiclePicker/FormVehiclePicker"
import { IFormComponentsProps } from "./formcomponents.interface"
import {
  FormFieldComponentEnum
} from "pages/customer/pages/settings/pages/users/pages/edit/UserCustomField.interfaces"
import { bySortValue } from "components/Form/FormTemplate/FormTemplate"
import { useTranslation } from "react-i18next"
import FormCompanyPicker from "components/Form/FormComponents/FormCompanyPicker/FormCompanyPicker"
import FormRadioButtons, { IFormRadioButtons } from "components/Form/FormComponents/FormRadioButtons/FormRadioButtons"
import FormBPLStationPicker from "components/Form/FormComponents/FormBPLStationPicker/FormBPLStationPicker"
import {
  FormikComputedProps,
  FormikHandlers,
  FormikHelpers, FormikRegistration,
  FormikSharedConfig,
  FormikState,
  FormikValues
} from "formik";
import { IFormFieldProps } from "components/Form/FormField/formfield.interface";
import { TFunction } from "i18next";
import FormUserPicker from "./FormUserPicker/FormUserPicker"

function isJsonString(str: string) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

const parseAndWrapJson = (value: string): Omit<UploadData, "Data">[] => {
  let parsedData: Omit<UploadData, "Data">[] = []
  if (Array.isArray(value)) return value
  try {
    parsedData = JSON.parse(value)
  } catch (e) {
    console.error("Error parsing JSON", e)
  }

  if (!Array.isArray(parsedData)) {
    parsedData = [parsedData]
  }

  return parsedData
}

interface IAttachmentFile {
  fileId: string
  fileName: string
}

interface ExtractedParams extends
  FormikSharedConfig<{}>,
  FormikState<FormikValues>,
  FormikHelpers<FormikValues>,
  FormikHandlers,
  FormikComputedProps<FormikValues>,
  FormikRegistration {
  submitForm: () => Promise<any>
}

interface IFormFieldLabel {
  labelKey?: string;
  labelDefault?: string;
}

const validateField = (t: TFunction<"translation", undefined>, componentProps: IFormFieldLabel, formikProps: ExtractedParams, field: IFormFieldProps, value: string) => {
  const validationText = t(`${componentProps.labelKey || ""}`, componentProps.labelDefault || "Field")
    + " " + `${t("global.is_required", { defaultValue: "is required" })}`
  if (formikProps.errors[field.id] && value) {
    formikProps.setFieldError(field.id.toString(), undefined);
  } else if (!value && !field.optional) {
    formikProps.setFieldError(field.id.toString(), validationText);
    formikProps.setFieldTouched(field.id.toString(), true, false);
  }
};

const FormComponents: React.FC<IFormComponentsProps> = (props) => {
  const { field, handleNext, formikProps, children, feature, translationData } = props
  const [deleteFileResult, deleteFile] = useApi() as [IApiResult, IApiFunc]
  const [fileUploadResult, uploadFile] = useApi() as [IApiResult, IApiFunc]
  const prefix = translationData?.formTypeKey ? translationData?.formTypeKey + "." : ""
  const { t } = useTranslation()

  useEffect(() => {
    if (field.component === FormFieldComponentEnum.Upload) {
      const dataExists = formikProps.values[field.id] !== "" && formikProps.values[field.id] !== null
      if (!dataExists) return
      const jsonData = parseAndWrapJson(formikProps.values[field.id])
      if (jsonData.length === 0) {
        formikProps.setFieldValue(field.id.toString(), "")
      } else {
        const filesWithoutData = jsonData.map(({ fileId, fileName }: IAttachmentFile) => ({
          fileId,
          fileName
        }))
        formikProps.setFieldValue(field.id.toString(), JSON.stringify(filesWithoutData))
      }
    }
  }, [field.component])

  useEffect(() => {
    if (fileUploadResult.status === 2) {
      const newFiles =
        formikProps.values[field.id] !== null && formikProps.values[field.id] !== ""
          ? [...JSON.parse(formikProps.values[field.id]), ...fileUploadResult.data]
          : fileUploadResult.data

      const filesWithoutData = newFiles.map(({ fileId, fileName }: IAttachmentFile) => ({
        fileId,
        fileName
      }))
      formikProps.setFieldValue(field.id.toString(), JSON.stringify(filesWithoutData))
    }
  }, [fileUploadResult])

  switch (field?.component) {
    case FormFieldComponentEnum.CardButton:
      return (
        <>
          {field?.options?.map((option, index) => {
            const componentProps: IFormCardButtonProps = {
              description: option?.properties?.descriptionDefault,
              title: option?.labelDefault,
              t: t,
              icon: option?.properties?.icon as IconDefinition,
              size: "2x",
              option: option
            }
            return (
              <FormCardButton
                {...componentProps}
                handleClick={(e: string) => {
                  formikProps.setFieldValue(field.id.toString(), e)
                  handleNext()
                }}
              />
            )
          })}
        </>
      )
    case FormFieldComponentEnum.RadioButtons: {
      const componentProps: IFormRadioButtons = {
        ...field,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        options: field.options?.map((option) => ({
          ...option,
          labelKey: prefix + option.labelKey,
          descriptionKey: prefix + option.descriptionKey,
        })),
        value: formikProps.values[field.id],
        error: !!(formikProps.touched[field.id] && formikProps.errors[field.id]),
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? t("pick_one", "Pick one option") : null
      }
      return (
        <FormRadioButtons
          {...componentProps}
          handleClick={(e: string) => {
            formikProps.setFieldValue(field.id.toString(), e);

            if (formikProps.errors[field.id]) {
              formikProps.setFieldError(field.id.toString(), undefined);
            }
          }}
        />
      )
    }
    case FormFieldComponentEnum.BigRadioButtons: {
      const componentProps: IRadioCardGroupProps = {
        ...field,
        options: field.options?.map((option) => ({
          ...option,
          labelKey: prefix + option.labelKey,
          descriptionKey: prefix + option.descriptionKey,
        })),
        value: formikProps.values[field.id]
      }
      return (
        <FormBigRadioButtons
          {...componentProps}
          handleClick={(e: string) => formikProps.setFieldValue(field.id.toString(), e)}
        />
      )
    }

    case FormFieldComponentEnum.Checkbox: {
      const componentProps: IFormCheckboxProps = {
        ...field,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        value: formikProps.values[field.id] === "true" || formikProps.values[field.id] === true ? true : false
      }

      return (
        <FormCheckbox
          {...componentProps}
          handleChange={(e: boolean) => {
            formikProps.setFieldValue(field.id.toString(), e)
            validateField(t, componentProps, formikProps, field, e.toString());
          }}
        />
      )
    }

    case FormFieldComponentEnum.Switch: {
      const componentProps: IFormSwitchProps = {
        ...field,
        labelKey: prefix + field.labelKey,
        value: formikProps.values[field.id] === "true" || formikProps.values[field.id] === true,
        handleChange: (val: boolean) => formikProps.setFieldValue(field.id.toString(), val.toString())
      }

      return <FormSwitch {...componentProps} />
    }

    case FormFieldComponentEnum.LocationPicker: {
      const componentProps: IFormLocationPickerProps = {
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        latLng:
          formikProps.values[field.id] && isJsonString(formikProps.values[field.id])
            ? JSON.parse(formikProps.values[field.id]).value
            : null,
        value: "",
        optional: field.optional,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <FormLocationPicker
          {...componentProps}
          handleChange={(e: string) => {
            formikProps.setFieldValue(field.id.toString(), e)
            validateField(t, componentProps, formikProps, field, e)
          }}
        />
      )
    }
    case FormFieldComponentEnum.DateTimePicker: {
      const componentProps: IFormDateFieldProps = {
        id: field.id,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        time: true,
        value: formikProps.values[field.id],
        optional: field.optional,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <FormDateTimePicker
          translationKey={field.labelKey}
          {...componentProps}
          handleChange={(e: string) => {
            formikProps.setFieldValue(field.id.toString(), e)
            validateField(t, componentProps, formikProps, field, e);
          }}
        />
      )
    }
    case FormFieldComponentEnum.DatePicker: {
      const componentProps: IFormDateFieldProps = {
        id: field.id,
        time: false,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        value: formikProps.values[field.id],
        optional: field.optional,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <FormDateTimePicker
          translationKey={field.labelKey}
          {...componentProps}
          handleChange={(e: string) => {
            formikProps.setFieldValue(field.id.toString(), e)
            validateField(t, componentProps, formikProps, field, e)
          }}
        />
      )
    }
    case FormFieldComponentEnum.VehiclePicker: {
      const componentProps: any = {
        field: field,
        placeholder: t("search_vehicle", "Search a vehicle"),
        value: formikProps.values[field.id.toString()],
        optional: field.optional,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormVehiclePicker
            {...componentProps}
            handleChange={(e: string) => {
              formikProps.setFieldValue(field.id.toString(), e)
              validateField(t, componentProps, formikProps, field, e)
            }}
            showInternNumber
          />
        </Grid>
      )
    }
    case FormFieldComponentEnum.DepartmentPicker: {
      const componentProps: any = {
        field: field,
        placeholder: t("search_department", "Search a department"),
        value: formikProps.values[field.id.toString()],
        optional: field.optional,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormDepartmentPicker
            {...componentProps}
            handleChange={(e: string) => {
              formikProps.setFieldValue(field.id.toString(), e)
              formikProps.setFieldTouched(field.id.toString(), true, false)
            }}
          />
        </Grid>
      )
    }
    case FormFieldComponentEnum.CompanyPicker: {
      const componentProps: any = {
        field: field,
        placeholder: t("search_company", "Search a company"),
        value: formikProps.values[field.id.toString()],
        optional: field.optional,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormCompanyPicker
            {...componentProps}
            handleChange={(e: string) => {
              formikProps.setFieldValue(field.id.toString(), e)
              validateField(t, componentProps, formikProps, field, e)
            }}
          />
        </Grid>
      )
    }
    case FormFieldComponentEnum.MultiTextField: {
      const componentProps: IFormTextFieldProps = {
        ...field,
        labelKey: prefix + field.labelKey,
        id: field.id.toString(),
        value: formikProps.values[field.id],
        rows: 4,
        optional: field.optional,
        dataType: field.dataType,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormTextField
            {...componentProps}
            handleChange={(e) => {
              formikProps.handleChange(e)
              console.log("e:", e)
              validateField(t, componentProps, formikProps, field, e.target.value)
            }}
            handleBlur={formikProps.handleBlur}
          />
        </Grid>
      )
    }

    case FormFieldComponentEnum.TextField: {
      const componentProps: IFormTextFieldProps = {
        ...field,
        labelKey: prefix + field.labelKey,
        id: field.id.toString(),
        value: formikProps.values[field.id],
        optional: field.optional,
        dataType: field.dataType,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormTextField
            {...componentProps}
            handleChange={(e) => {
              formikProps.handleChange(e)
              validateField(t, componentProps, formikProps, field, e.target.value);
            }}
            handleBlur={formikProps.handleBlur}
          />
        </Grid>
      )
    }

    case FormFieldComponentEnum.Select: {
      const selectOptions: Array<{ label: string; value: string, labelKey?: string, descriptionKey?: string, sort?: number }> = field.options
        ? field.options?.map((option: IOptionProps) => ({ label: option.labelDefault, value: option.value, labelKey: option.labelKey, sort: option.sort }))
        : []
      const find = selectOptions?.find((i) => i.value.toString() === formikProps.values[field.id])
      const componentProps: IFormSelectProps = {
        id: field.id.toString(),
        value: find ? { ...find, labelKey: translationData?.formTypeKey + "." + find.labelKey } : null,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        options: selectOptions?.map((option) => ({
          ...option,
          labelKey: prefix + option.labelKey,
          descriptionKey: prefix + option.descriptionKey,
        }))?.sort(bySortValue),
        optional: field.optional,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }
      return (
        <Grid md={12} xs={12}>
          <FormSelect
            {...componentProps}
            handleChange={(_: any, value: string) => {
              formikProps.setFieldValue(field.id.toString(), value)
              validateField(t, componentProps, formikProps, field, value)
            }}
          />
        </Grid>
      )
    }

    case FormFieldComponentEnum.BPLStationPicker: {
      const componentProps: any = {
        field: field,
        placeholder: t("search_vehicle", "Search a vehicle"),
        value: formikProps.values[field.id.toString()],
        optional: field.optional,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }

      return (
        <Grid md={12} xs={12}>
          <FormBPLStationPicker
            {...componentProps}
            handleChange={(e: string) => {
              formikProps.setFieldValue(field.id.toString(), e)
              validateField(t, componentProps, formikProps, field, e)
            }}
          />
        </Grid>
      )
    }
    case FormFieldComponentEnum.UserPicker: {
      const componentProps: any = {
        field: field,
        placeholder: t("choose_user", "Choose user"),
        value: formikProps.values[field.id.toString()],
        optional: field.optional,
        labelKey: prefix + field.labelKey,
        labelDefault: field.labelDefault,
        handleBlur: formikProps.handleBlur,
        error: formikProps.touched[field.id] && formikProps.errors[field.id] ? true : false,
        helperText:
          formikProps.touched[field.id] && formikProps.errors[field.id] ? formikProps.errors[field.id] : null
      }

      return (
        <Grid md={12} xs={12}>
          <FormUserPicker
            {...componentProps}
            handleChange={(_: any, e: string) => {
              formikProps.setFieldValue(field.id.toString(), e)
              validateField(t, componentProps, formikProps, field, e)
            }}
          />
        </Grid>
      )
    }
    case FormFieldComponentEnum.Upload:
      const componentProps: IFormUploadFileProps | any = {
        ...field,
        labelKey: prefix + field.labelKey,
        featureName: feature || "deviation",
        files:
          formikProps.values[field.id] !== null && formikProps.values[field.id] !== ""
            ? formikProps.values[field.id]
            : [],
        label: "Ladda",
        loading: false
      }

      const title = feature === "usercustomfield"
        ? field.labelDefault
        : field.labelKey
          ? t(`global.${field.labelKey}`, { defaultValue: "Images" })
          : field.labelKey

      return (
        <Grid md={12} xs={12}>
          <StyledFormLabel>
            <Typography variant="labelLarge" fontWeight={500}>
              {title}{" "}
              {!field.optional && "*"}
            </Typography>
          </StyledFormLabel>

          {feature === "usercustomfield" &&
            formikProps.values[field.id] !== null &&
            formikProps.values[field.id] !== "" ? (
            <></>
          ) : (
            <FormUploadFile
              acceptedFileFormat={{
                "image/jpeg": [".jpg", ".jpeg", ".jfif"],
                "image/png": [".png"],
                "application/pdf": [".pdf"],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                "application/vnd.ms-excel": [".xls"]
              }}
              compressToSize={1.9 * 1024 * 1024}
              {...componentProps}
              handleChange={(files: File[]) => {
                if (feature === "usercustomfield") {
                  uploadFile.file(
                    apiEndpoints.attachment.addattachment.replace("{feature}", componentProps.featureName),
                    files[0]
                  )
                } else {
                  files.forEach((file) => {
                    uploadFile.file(
                      apiEndpoints.attachment.addattachment.replace("{feature}", componentProps.featureName),
                      file
                    )
                  })
                }
              }}
            />
          )}
          <FormUploadPreviewList
            handleRemove={(attachment: IFormUploadAttachmentProps) => {
              if (feature === "usercustomfield") {
                const feature = attachment.fileId.split("/")[0]
                const fileId = attachment.fileId.split("/")[1]
                const url =
                  apiEndpoints.attachment.deleteattachment.replace("{fileid}", fileId) + `?feature=${feature}`
                formikProps.setFieldValue(field.id.toString(), "")
                deleteFile.del(url)
                formikProps.handleSubmit()
              } else {
                const url = apiEndpoints.attachment.deleteattachment.replace(
                  "{fileid}",
                  attachment.fileId.replace("/", "%2F")
                )
                deleteFile.del(url)
                const newFiles = JSON.parse(formikProps.values[field.id])?.filter(
                  (x: IFormUploadAttachmentProps) => x.fileId !== attachment.fileId
                )
                formikProps.setFieldValue(field.id.toString(), JSON.stringify(newFiles))
              }
            }}
            files={
              formikProps.values[field.id] && formikProps.values[field.id] !== ""
                ? typeof formikProps.values[field.id] === "string"
                  ? JSON.parse(formikProps.values[field.id])
                  : formikProps.values[field.id]
                : []
            }
            feature={feature}
          />
        </Grid>
      )

    default:
      return <>{children}</>
  }
}

export default FormComponents
